// DailyPausePage — Explains the Daily Pause feature
// Mirrors marketing: positions auto-flatten at 16:00 CT, account freezes until 16:05 next day
// Includes: live EOD countdown timer, today's P&L, freeze toggle, "I'm done for the day" button

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { TfCard, TfCardFloating, TfSectionHeaderView, TfStatRow } from '../../components/TfPrimitives';

export function DailyPausePage() {
  const nav = useNavigate();
  const [now, setNow] = useState(new Date());
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  // EOD is 16:00 CT (Chicago time). For demo, use 16:00 local.
  const eod = new Date(now);
  eod.setHours(16, 0, 0, 0);
  if (eod < now) eod.setDate(eod.getDate() + 1);
  const ms = eod.getTime() - now.getTime();
  const hours = Math.floor(ms / 3600000);
  const mins = Math.floor((ms % 3600000) / 60000);
  const secs = Math.floor((ms % 60000) / 1000);

  return (
    <div className="min-h-screen bg-tf-surface-1 pb-8">
      <div className="sticky top-0 z-10 flex h-11 items-center justify-between border-b border-tf-border ios26-navbar px-2 backdrop-blur-xl">
        <button onClick={() => nav(-1)} data-testid="back" className="flex h-9 w-9 items-center justify-center text-tf-blue haptic">
          <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2.4"><path d="M15 5l-7 7 7 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </button>
        <div className="absolute left-1/2 -translate-x-1/2 text-[15px] font-semibold">Daily Pause</div>
        <div className="w-9" />
      </div>

      {/* Hero — countdown timer + pill buttons */}
      <div className="mx-4 mt-4">
        <TfCardFloating className="text-center">
          <div className="text-[13px]" style={{ color: '#8E8E93' }}>End of day in</div>
          <div className="mt-1 flex items-baseline justify-center gap-1">
            <span className="text-[40px] font-bold tabular-nums leading-none" style={{ color: '#1C1C1E' }}>
              {String(hours).padStart(2, '0')}:{String(mins).padStart(2, '0')}:{String(secs).padStart(2, '0')}
            </span>
          </div>
          <div className="mt-1 flex items-center justify-center gap-1 text-[13px]">
            <span className="text-tf-text-3">Positions auto-flatten at 16:00 CT</span>
          </div>
          <div className="mt-4 grid grid-cols-2 gap-3">
            <button onClick={() => setPaused(true)} data-testid="pause-now" className="flex h-12 items-center justify-center gap-1.5 rounded-full bg-tf-text-1 text-[15px] font-semibold text-white active:scale-[0.97] transition-transform">
              Pause now
              <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M7 17L17 7" /><path d="M7 7h10v10" />
              </svg>
            </button>
            <button onClick={() => setPaused(false)} className="flex h-12 items-center justify-center gap-1.5 rounded-full bg-tf-text-1 text-[15px] font-semibold text-white active:scale-[0.97] transition-transform">
              {paused ? 'Resume' : 'Continue'}
              <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M17 7L7 17" /><path d="M17 17H7V7" />
              </svg>
            </button>
          </div>
          <div className="mt-3 flex items-center justify-center gap-2">
            <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: paused ? '#34C759' : '#FF9500' }} />
            <span className="text-[12px] font-semibold">{paused ? '🟢 Active' : '⏸ Paused'}</span>
          </div>
        </TfCardFloating>
      </div>

      {/* 3-up stat row for today's status */}
      <TfStatRow
        className="mt-4"
        stats={[
          { label: 'EOD balance', value: '$52,840', color: '#1C1C1E' },
          { label: 'Today P&L', value: '+$2,840', color: '#34C759' },
          { label: 'Open pos.', value: '2', color: '#FF9500' },
        ]}
      />

      {/* Hero explanation */}
      <div className="mx-4 mt-3">
        <TfCard className="p-4">
          <h2 className="text-[14px] font-bold text-tf-text-1">What is Daily Pause?</h2>
          <p className="mt-1.5 text-[13px] text-tf-text-2 leading-relaxed">
            At 16:00 CT, all open positions are automatically closed. Your account balance is locked until 16:05 CT the next trading day. This prevents overnight gap risk and protects you from rollover losses.
          </p>
        </TfCard>
      </div>

      {/* How it works */}
      <TfSectionHeaderView title="How it works" />
      <div className="mx-4">
        <TfCard>
          {[
            { time: '15:55 CT', label: '5-min warning', sub: 'Heads-up email if you have open positions', icon: '⏰' },
            { time: '16:00 CT', label: 'Auto-flatten',   sub: 'All positions closed at market price',         icon: '🔒' },
            { time: '16:05 CT', label: 'Account frozen',  sub: 'No new trades until next day',                icon: '🧊' },
            { time: '16:00 CT', label: 'Daily reset',     sub: 'Loss limit reset for next day based on EOD balance', icon: '🔄' },
          ].map((s, i, a) => (
            <div key={s.label} data-testid={`step-${i}`} className={['flex items-start gap-3 px-4 py-3', i < a.length - 1 ? 'border-b border-tf-border' : ''].join(' ')}>
              <div className="text-[24px]">{s.icon}</div>
              <div className="flex-1">
                <div className="flex items-center gap-1.5">
                  <span className="text-[10px] font-bold uppercase text-tf-text-3 tabular-nums">{s.time}</span>
                </div>
                <div className="text-[14px] font-semibold text-tf-text-1">{s.label}</div>
                <div className="text-[11px] text-tf-text-3">{s.sub}</div>
              </div>
            </div>
          ))}
        </TfCard>
      </div>

      {/* FAQ */}
      <TfSectionHeaderView title="Common questions" />
      <div className="mx-4">
        <TfCard>
          {[
            { q: 'Can I disable Daily Pause?', a: 'No. Daily Pause is mandatory to protect your account from overnight risk.' },
            { q: 'What if I have no open positions at 16:00 CT?', a: 'Nothing happens. The pause only affects accounts with active positions.' },
            { q: 'Does it count against my daily loss limit?', a: 'No. Daily Pause happens BEFORE daily loss is calculated. Your new day limit is based on your 16:00 CT balance.' },
            { q: 'Can I trade overnight on purpose?', a: 'No. All positions are auto-closed. This is by design to prevent gap risk.' },
          ].map((f, i, a) => (
            <details key={i} data-testid={`pause-faq-${i}`} className={['px-4 py-3', i < a.length - 1 ? 'border-b border-tf-border' : ''].join(' ')}>
              <summary className="text-[14px] font-semibold text-tf-text-1 cursor-pointer">{f.q}</summary>
              <p className="mt-2 text-[13px] text-tf-text-2 leading-relaxed">{f.a}</p>
            </details>
          ))}
        </TfCard>
      </div>
    </div>
  );
}
